import { useState, useEffect } from "react";

import { Drawer, Icons, Button, Form, Text } from "../../design";
import { useAxios, handleError, transformMultilingualData } from "../../utils/helpers";
import { useLoop } from "../loop-provider";
import { translate } from "../../utils/translations";
import ProductCard from "../cards/product";

function SelectProductSubscriptionDrawer(props: any) {
    const [axios] = useAxios();
    const { myshopify_domain, token, locale, localeRoute } = useLoop();
    const [isLoading, setisLoading] = useState(false);
    const [products, setproducts] = useState([]);
    const [totalPages, settotalPages] = useState(0);

    const [search, setsearch] = useState("");
    const [page, setpage] = useState(1);

    const loadProducts = async (args: any = {}) => {
        setisLoading(true);
        try {
            const countryCode = (window as any)?.Shopify?.country || "__ALL__";
            let _search = search;
            if ("search" in args) {
                _search = args.search;
            }
            let _page = page;
            if ("page" in args) {
                _page = args.page;
            }
            let _params: any = {
                search: _search,
                page: _page,
                myshopify_domain: myshopify_domain,
                token: token,
                countryCode: countryCode,
            };

            const [subscriptionProducts, storeDetails] = await Promise.all([
                axios.get(
                    `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/read_subscription_products?` +
                        new URLSearchParams(_params),
                ),
                axios.get(`${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/store_details`),
            ]);

            const { data } = subscriptionProducts;
            const storeDefaultLocale: string = storeDetails?.data.data.default_locale;

            if (storeDefaultLocale != null && storeDefaultLocale != locale)
                await transformMultilingualData(
                    data.data.results,
                    myshopify_domain,
                    locale,
                    storeDefaultLocale,
                    localeRoute,
                );

            if (args.reset) {
                setproducts([...data.data.results]);
            } else {
                setproducts([...products, ...data.data.results]);
            }
            settotalPages(data.data.total_pages);
            setpage(_page);
        } catch (error) {
            handleError(error, "components/drawers/select-product-subscription", "loadProducts", {});
        }
        setisLoading(false);
    };

    const loadMoreIfNeeded = async () => {
        if (products.length < 3 && page < totalPages && !isLoading) {
            await loadProducts({ page: page + 1 });
        }
    };

    useEffect(() => {
        loadMoreIfNeeded();
    }, [products]);

    const onBottomReached = () => {
        if (page < totalPages && !isLoading) {
            loadProducts({ page: page + 1 }).then(() => {
                loadMoreIfNeeded();
            });
        }
    };

    useEffect(() => {
        loadProducts();
    }, []);

    return (
        <Drawer
            id="select-product"
            title={translate("select_product_drawer_header")}
            active={props.active}
            toggleActive={props.toggleActive}
        >
            <div className="d-flex p-3 border-bottom align-items-center">
                <Form.Input
                    placeholder={translate("select_product_search_placeholder")}
                    value={search}
                    onChange={(event: any) => {
                        setsearch(event.target.value);
                        if (event.target.value == "") {
                            loadProducts({ reset: true, page: 1, search: "" });
                        }
                    }}
                    clear={true}
                    onEnter={() => {
                        loadProducts({ reset: true, page: 1 });
                    }}
                />
                <Button
                    className="ms-2"
                    size="sm"
                    onClick={() => {
                        loadProducts({ reset: true, page: 1 });
                    }}
                >
                    {translate("select_product_search_button")}
                </Button>
            </div>
            <Drawer.ScrollableBody
                id="loop-create-subscription-product-select-drawer-body"
                onBottomReached={onBottomReached}
            >
                {products.length == 0 ? (
                    <Text.P2 className="text-center my-1 loop-text-muted">
                        {translate("select_product_no_products_found")}
                    </Text.P2>
                ) : (
                    <>
                        {products.map((product, i) => {
                            return (
                                <div className="mb-3" key={i}>
                                    <ProductCard
                                        key={i}
                                        product={product}
                                        onProductSelect={props.handleProductSelect}
                                        type="create-select-drawer"
                                    />
                                </div>
                            );
                        })}
                    </>
                )}

                {isLoading ? (
                    <Icons.Spinner />
                ) : page >= totalPages ? (
                    <div className="text-center my-1 loop-text-muted">{translate("end_of_list_label")}</div>
                ) : null}
            </Drawer.ScrollableBody>
            {props.hideBackButton ? null : (
                <Drawer.Footer>
                    <div className="d-flex flex-column">
                        <Button size="lg" variant="underline" className="m-2" onClick={props.toggleActive}>
                            {translate("go_back_button")}
                        </Button>
                    </div>
                </Drawer.Footer>
            )}
        </Drawer>
    );
}

export default SelectProductSubscriptionDrawer;
